import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import {
  operatorConsole,
  type TestPlayer,
  type TestPlayersAddResponse,
  type TestPlayersDeactivateResponse,
  type TestPlayersStatusFilter,
} from '../../lib/api/operatorConsole';
import { AddTestPlayersForm } from './AddTestPlayersForm';
import { AddResultPanel } from './AddResultPanel';
import { backendMessage } from './backendMessage';
import { buildDeactivationReport, type DeactivationReport } from './deactivationReport';
import { DeactivateResultPanel } from './DeactivateResultPanel';
import { hasParsableId } from './hasParsableId';
import { TestPlayersList } from './TestPlayersList';

const PER_PAGE = 25;

// Long enough that a typed id travels as one request, short enough that the
// list still feels like it follows the keyboard.
const SEARCH_DEBOUNCE_MS = 300;

// The gate is a state of this screen rather than the outcome of one request:
// the partner needs to know the list stays readable and who to ask, which the
// backend's own message does not say.
const DISABLED_MESSAGE =
  'Editing the registry is disabled for your account — the list stays visible. Contact us to manage it yourself.';

const NO_IDS_MESSAGE =
  'We could not read a single player id out of what you sent. Put one id per line — commas, semicolons and a pasted CSV with an account_id column all work.';

const REFUSED_WITHOUT_MESSAGE = 'The registry did not lift this mark.';

export function TestPlayersPage() {
  const [players, setPlayers] = useState<TestPlayer[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<TestPlayersStatusFilter>('active');
  const [search, setSearch] = useState('');
  // What the last request actually filtered on — the input runs ahead of it.
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [addResult, setAddResult] = useState<TestPlayersAddResponse | null>(null);
  const [deactivateReport, setDeactivateReport] = useState<DeactivationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadPlayers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await operatorConsole.listTestPlayers({
        status,
        page,
        per: PER_PAGE,
        ...(appliedSearch ? { search: appliedSearch } : {}),
      });
      setPlayers(res.test_players);
      setTotalCount(res.meta.total_count);
      setSelected([]);
    } catch (err) {
      setProblem(`Failed to load test players: ${backendMessage(err) ?? 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [status, page, appliedSearch]);

  useEffect(() => {
    void loadPlayers();
  }, [loadPlayers]);

  // The registry can hold thousands of ids, so the term goes to the backend
  // rather than filtering the page that happens to be loaded.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const readGate = useCallback(async () => {
    const me = await operatorConsole.me();
    return me.operator.allow_test_player_registry === true;
  }, []);

  useEffect(() => {
    let cancelled = false;
    readGate()
      .then((allowed) => {
        if (!cancelled) setCanEdit(allowed);
      })
      .catch(() => {
        // Flag stays off — the page degrades to the read-only view.
      });
    return () => {
      cancelled = true;
    };
  }, [readGate]);

  function flashSuccess(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 2500);
  }

  async function gateStillOpen(): Promise<boolean> {
    try {
      const allowed = await readGate();
      setCanEdit(allowed);
      return allowed;
    } catch {
      // /me is the only authority on the gate; unreachable, we leave the page
      // as it is and let the request's own message speak.
      return true;
    }
  }

  function submissionProblem(err: unknown, submitted: string): string {
    if (err instanceof ApiError && err.status === 422 && !hasParsableId(submitted)) {
      // With nothing id-shaped in the payload, `no_account_ids` is the only
      // 422 the backend can answer with, and it words that one in terms of the
      // request fields. Say it in the partner's terms instead.
      return NO_IDS_MESSAGE;
    }
    return backendMessage(err) ?? 'The registry did not accept this batch.';
  }

  async function handleAdd(text: string, reason: string): Promise<boolean> {
    setProblem(null);
    setDeactivateReport(null);
    try {
      const res = await operatorConsole.addTestPlayers({
        csv: text,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setAddResult(res);
      flashSuccess('Registry updated');
      setPage(1);
      await loadPlayers();
      return true;
    } catch (err) {
      // Adding has a single 403: the gate. There is no per-row refusal on this
      // path, so the page collapses to its read-only shape.
      if (err instanceof ApiError && err.status === 403) {
        setCanEdit(false);
        setProblem(DISABLED_MESSAGE);
        return false;
      }
      setProblem(submissionProblem(err, text));
      return false;
    }
  }

  // A 2xx here means the request was processed, not that any mark was lifted:
  // rows we placed come back as `forbidden`. The report decides what is said.
  async function runDeactivation(
    request: () => Promise<TestPlayersDeactivateResponse>,
    successText: (report: DeactivationReport) => string,
  ) {
    setProblem(null);
    setDeactivateReport(null);
    setAddResult(null);
    try {
      const report = buildDeactivationReport(await request());
      if (report.clean) {
        flashSuccess(successText(report));
      } else {
        setDeactivateReport(report);
      }
      await loadPlayers();
    } catch (err) {
      await reportDeactivationError(err);
    }
  }

  async function reportDeactivationError(err: unknown) {
    if (err instanceof ApiError && err.status === 403) {
      // Two different 403s arrive here: the registry gate closing on this
      // operator, and a mark only we may lift. /me settles which, so a refused
      // row does not read as lost access.
      const stillAllowed = await gateStillOpen();
      setProblem(
        stillAllowed ? (backendMessage(err) ?? REFUSED_WITHOUT_MESSAGE) : DISABLED_MESSAGE,
      );
      return;
    }
    if (err instanceof ApiError && err.status === 422) {
      // The request itself was not readable — `no_account_ids` and friends.
      // The backend names the field it wanted, so it is quoted as it came.
      setProblem(backendMessage(err) ?? 'The registry did not accept this request.');
      return;
    }
    setProblem(`Failed to deactivate: ${backendMessage(err) ?? 'unknown error'}`);
  }

  function handleDeactivate(player: TestPlayer) {
    const confirmed = window.confirm(
      `Stop excluding ${player.account_id}? Its whole history counts towards your reports again.`,
    );
    if (!confirmed) return;

    void runDeactivation(
      () => operatorConsole.deleteTestPlayer(player.account_id),
      () => `${player.account_id} deactivated`,
    );
  }

  function handleBulkDeactivate() {
    if (selected.length === 0) return;

    const confirmed = window.confirm(
      `Stop excluding ${selected.length} selected player${selected.length === 1 ? '' : 's'}? Their whole history counts towards your reports again.`,
    );
    if (!confirmed) return;

    const submitted = selected;
    void runDeactivation(
      () => operatorConsole.deactivateTestPlayers(submitted),
      (report) =>
        `${report.deactivated} player${report.deactivated === 1 ? '' : 's'} deactivated`,
    );
  }

  return (
    <div className="p-6">
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold text-[var(--co-text)]">Test players</h1>
          <p className="text-[12px] text-[var(--co-muted)]">
            Activity from these player accounts is excluded from your reporting and from the
            figures we settle on — across their whole history, not just from today.
          </p>
        </div>
        {success && (
          <span className="rounded bg-[var(--co-success)] px-2 py-1 text-[11px] font-medium text-white">
            {success}
          </span>
        )}
      </div>

      {problem && (
        <div
          role="alert"
          className="mb-5 max-w-3xl rounded-xl px-4 py-3 text-[12px]"
          style={{
            color: 'var(--co-error)',
            backgroundColor: 'color-mix(in srgb, var(--co-error) 10%, transparent)',
          }}
        >
          {problem}
        </div>
      )}

      {canEdit ? (
        <AddTestPlayersForm onSubmit={handleAdd} />
      ) : (
        <p className="mb-5 max-w-2xl rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4 text-[12px] text-[var(--co-muted)]">
          This list is read-only for your account: you can see every excluded id, but only we
          can change it. Contact us to manage it yourself.
        </p>
      )}

      {addResult && (
        <AddResultPanel result={addResult} onDismiss={() => setAddResult(null)} />
      )}

      {deactivateReport && (
        <DeactivateResultPanel
          report={deactivateReport}
          onDismiss={() => setDeactivateReport(null)}
        />
      )}

      <TestPlayersList
        players={players}
        loading={loading}
        canEdit={canEdit}
        status={status}
        onStatusChange={(next) => {
          setStatus(next);
          setPage(1);
        }}
        search={search}
        onSearchChange={setSearch}
        page={page}
        perPage={PER_PAGE}
        totalCount={totalCount}
        onPageChange={setPage}
        selected={selected}
        onSelectedChange={setSelected}
        onDeactivate={handleDeactivate}
        onBulkDeactivate={handleBulkDeactivate}
      />
    </div>
  );
}
